import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Clock, 
  CheckCircle, 
  FileText, 
  Users, 
  Star, 
  Shield, 
  Target, 
  ArrowRight,
  Calendar,
  MapPin,
  Award,
  Zap
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import '@/styles/animations.css';

const TemporaryResidenceCard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const features = [
    {
      title: "Legal Residence",
      description: "Live and work legally in Poland for up to 3 years",
      icon: Shield
    },
    {
      title: "Work Authorization",
      description: "Automatic right to work without additional permits",
      icon: Users
    },
    {
      title: "EU Travel",
      description: "Travel freely within the Schengen area",
      icon: MapPin
    },
    {
      title: "Renewable",
      description: "Can be extended for additional periods",
      icon: Calendar
    }
  ];

  const requirements = [
    "Valid passport with at least 6 months validity",
    "Proof of accommodation in Poland",
    "Health insurance coverage",
    "Sufficient financial means",
    "Clean criminal record",
    "Purpose of stay documentation"
  ];

  const process = [
    {
      step: "1",
      title: "Initial Consultation",
      description: "Free assessment of your eligibility and requirements"
    },
    {
      step: "2",
      title: "Document Preparation",
      description: "We help you gather and prepare all necessary documents"
    },
    {
      step: "3",
      title: "Application Submission",
      description: "Submit your application to the appropriate voivodeship office"
    },
    {
      step: "4",
      title: "Processing & Follow-up",
      description: "Monitor your application status and handle any requests"
    },
    {
      step: "5",
      title: "Card Collection",
      description: "Collect your temporary residence card upon approval"
    }
  ];

  const benefits = [
    "Live and work legally in Poland",
    "Access to public healthcare",
    "Open bank accounts and sign contracts",
    "Travel within the Schengen area",
    "Bring family members to Poland",
    "Pathway to permanent residence"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-b from-blue-500/5 via-blue-600/10 to-background py-24 sm:py-32">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.5))] dark:bg-grid-black/10" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-blue-500/10 text-blue-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Clock className="h-4 w-4" />
              Temporary Residence Card
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-foreground mb-6 font-alumni animate-fade-in">
              Legal Residence in Poland
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind animate-subtle-slide-up mb-8">
              Obtain legal residence in Poland for up to 3 years. Our comprehensive service includes document preparation, 
              application submission, and guidance throughout the entire process.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={user ? "/dashboard" : "/register"}>
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white">
                  Start Application
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => navigate('/consultation')}
              >
                Free Consultation
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Features Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4 font-alumni">Key Benefits</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind">
              What you get with a Temporary Residence Card
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <Card key={index} className="text-center border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                  <CardHeader className="pb-4">
                    <div className="mx-auto bg-blue-500/10 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                      <IconComponent className="h-6 w-6 text-blue-600" />
                    </div>
                    <CardTitle className="text-lg font-bold text-foreground font-alumni">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-muted-foreground font-hind">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Requirements Section */}
        <div className="mb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-foreground mb-6 font-alumni">Requirements</h2>
              <p className="text-lg text-muted-foreground mb-8 font-hind">
                To qualify for a Temporary Residence Card, you must meet the following requirements:
              </p>
              <div className="space-y-4">
                {requirements.map((requirement, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-foreground font-hind">{requirement}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-blue-50 rounded-2xl p-8">
              <div className="text-center">
                <div className="bg-blue-500/10 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <Target className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-4 font-alumni">Processing Time</h3>
                <p className="text-3xl font-bold text-blue-600 mb-2">2-3 Months</p>
                <p className="text-muted-foreground font-hind">Average processing time from application to approval</p>
              </div>
            </div>
          </div>
        </div>

        {/* Process Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4 font-alumni">Application Process</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind">
              Our streamlined 5-step process makes your application smooth and stress-free
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {process.map((step, index) => (
              <div key={index} className="text-center">
                <div className="bg-blue-500/10 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <span className="text-2xl font-bold text-blue-600">{step.step}</span>
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2 font-alumni">{step.title}</h3>
                <p className="text-muted-foreground text-sm font-hind">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits Section */}
        <div className="mb-20">
          <div className="bg-gradient-to-br from-blue-500/5 to-blue-600/10 rounded-2xl p-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4 font-alumni">What You Can Do</h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind">
                With a Temporary Residence Card, you gain access to numerous opportunities in Poland
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-foreground font-hind">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-12 text-white">
            <h2 className="text-3xl font-bold mb-4 font-alumni">Ready to Start Your Application?</h2>
            <p className="text-xl mb-8 opacity-90 font-hind">
              Join hundreds of satisfied clients who have successfully obtained their Temporary Residence Card
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={user ? "/dashboard" : "/register"}>
                <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
                  Start Application
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white !text-blue-400 hover:bg-white/10 hover:!text-white"
                onClick={() => navigate('/consultation')}
              >
                Free Consultation
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TemporaryResidenceCard; 